import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, User } from "lucide-react";
import api from "@/lib/api";

export default function BlogPost() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get(`/blog/${slug}`)
            .then(({ data }) => setPost(data))
            .catch(() => setError("Post not found"));
    }, [slug]);

    if (error) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-center">
                <h1 className="font-serif text-3xl font-bold text-[#0A192F]">{error}</h1>
                <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-[#D4AF37] font-semibold">
                    <ArrowLeft className="w-4 h-4" /> Back to insights
                </Link>
            </div>
        );
    }
    if (!post) return <div className="py-32 text-center text-slate-500">Loading…</div>;

    return (
        <article data-testid="blog-post-page">
            <section className="bg-[#0A192F] text-white py-24">
                <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
                    <Link to="/blog" data-testid="blog-back-link" className="text-xs uppercase tracking-widest text-[#D4AF37] inline-flex items-center gap-2 mb-6">
                        <ArrowLeft className="w-3.5 h-3.5" /> All Insights
                    </Link>
                    <span className="overline">{post.category}</span>
                    <h1 className="font-serif text-4xl sm:text-5xl font-bold mt-4 leading-tight">
                        {post.title}
                    </h1>
                    <div className="mt-8 flex items-center gap-6 text-sm text-slate-300">
                        <span className="flex items-center gap-2">
                            <User className="w-4 h-4 text-[#D4AF37]" /> {post.author}
                        </span>
                        <span className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-[#D4AF37]" />
                            {new Date(post.published_at).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                            })}
                        </span>
                    </div>
                </div>
            </section>

            {post.cover_image && (
                <div className="max-w-5xl mx-auto -mt-12 px-6 relative z-10">
                    <div className="w-full aspect-[16/9] bg-slate-100 rounded-sm overflow-hidden flex items-center justify-center shadow-xl">
                        <img
                            src={post.cover_image}
                            alt={post.title}
                            className="w-full h-full object-contain"
                        />
                    </div>
                </div>
            )}

            <section className="py-20">
                <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
                    <p className="text-xl text-slate-600 italic leading-relaxed border-l-2 border-[#D4AF37] pl-6 mb-10">
                        {post.excerpt}
                    </p>
                    <div className="prose-content space-y-6 text-lg text-slate-700 leading-relaxed">
                        {post.content.split("\n\n").map((para, i) => (
                            <p key={i}>{para}</p>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-slate-50 py-16 border-t border-slate-200">
                <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
                    <h3 className="font-serif text-2xl font-bold text-[#0A192F]">
                        Want to turn this into action for your organization?
                    </h3>
                    <Link
                        to="/contact"
                        className="mt-6 inline-flex items-center gap-2 bg-[#0A192F] text-white hover:bg-[#1E3A8A] rounded-sm px-6 py-3 font-bold text-xs uppercase tracking-widest"
                    >
                        Schedule a Consultation
                    </Link>
                </div>
            </section>
        </article>
    );
}
